import { addMonths } from 'date-fns';
import { StorageKey } from './storageKeys';
import { DynamicFeatureFlag, type FeatureFlag } from 'app/utils/helpers';

interface PresentationContent {
  id: string;
  storageKey: StorageKey;
  existingAccountsOnly?: boolean;
}

export type FeaturePresentation = PresentationContent &
  (
    | { featureFlag: FeatureFlag | DynamicFeatureFlag; releasedAt?: never }
    | { featureFlag?: undefined; releasedAt: string | null }
  );

export function getPresentationReleaseDate(
  presentation: FeaturePresentation,
  flagReleaseDates: Readonly<Record<string, string | undefined>>,
): string | null | undefined {
  return presentation.featureFlag ? flagReleaseDates[presentation.featureFlag] : presentation.releasedAt;
}

/** Opt-in registry: feature flags without an entry never create announcements. */
export const FEATURE_PRESENTATIONS: readonly FeaturePresentation[] = [
  {
    id: 'guardian',
    storageKey: StorageKey.GuardianFeaturePresentationSeen,
    featureFlag: DynamicFeatureFlag.ListingGuardianship,
    existingAccountsOnly: true,
  },
];

export function readPresentationAccounts(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function selectFeaturePresentation({
  presentations,
  userId,
  accountCreatedAt,
  enabledFlags,
  flagReleaseDates = {},
  seenByKey,
  now = new Date(),
}: {
  presentations: readonly FeaturePresentation[];
  userId: string;
  accountCreatedAt: Date | string;
  enabledFlags: readonly string[];
  flagReleaseDates?: Readonly<Record<string, string | undefined>>;
  seenByKey: Partial<Record<StorageKey, readonly string[]>>;
  now?: Date;
}): FeaturePresentation | undefined {
  const enabledFlagSet = new Set(enabledFlags);
  return presentations
    .filter((presentation) => {
      const releasedAt = getPresentationReleaseDate(presentation, flagReleaseDates);
      if (!releasedAt) return false;
      const release = new Date(releasedAt);
      if (!Number.isFinite(release.getTime()) || now < release || now >= addMonths(release, 1)) return false;
      if (presentation.featureFlag && !enabledFlagSet.has(presentation.featureFlag)) return false;
      if (presentation.existingAccountsOnly && !(new Date(accountCreatedAt) < release)) return false;
      // A missing history means it has not loaded yet, rather than that it is empty.
      const seen = seenByKey[presentation.storageKey];
      return seen !== undefined && !seen.includes(userId);
    })
    .sort(
      (a, b) =>
        Date.parse(getPresentationReleaseDate(a, flagReleaseDates)!) -
        Date.parse(getPresentationReleaseDate(b, flagReleaseDates)!),
    )[0];
}
